import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarFrame } from '../../shared/components/AvatarFrame.js';
import type { CluegivingPlayer } from '../types.js';
import { ClueSlip } from '../../shared/components/ClueSlip.js';
import './CastGrid.css';

interface CastGridProps {
  /**
   * Players sorted ascending by `turnOrder`. The view layer owns the sort;
   * the grid renders the array as-is so the first cell is always the ghost-
   * chosen first speaker — no wrapping, no starting in the middle.
   */
  players: CluegivingPlayer[];
  /**
   * Ids of every Ghost in the round (incl. local viewer when local is a Ghost).
   * GHOSTS-tagged server-side, so this stays empty for Villager / Fool.
   */
  ghostIds: readonly string[];
  /** Local viewer's player id. Suppresses the fellow-Ghost mark on the
   *  local Ghost's own tile — "fellow" requires an other. */
  meId: string;
  /**
   * Id of the current active speaker. The cell whose `id` matches receives
   * the warm-shadow lift on its AvatarFrame. `null` in the terminal "all
   * clues are in" state — no cell carries the lift.
   */
  currentClueGiverId: string | null;
  /** Cold-arrival suppression for the per-cell ClueSlip ink-in (handoff §6). */
  justArrived?: boolean;
}

/**
 * The clue-giving cast band. Non-interactive: clue-giving has no voting, so
 * cells are `<li>` items rather than buttons. Each cell carries an
 * `AvatarFrame` and a `ClueSlip` beneath. The active speaker's cell gets the
 * warm-shadow lift on the frame (via `data-active-speaker='yes'`).
 *
 * Grid columns: 3 desktop, 4 mobile — matching shipped lobby
 * (`Lobby.css:84-99`). Spatial flow = turn order; cells render in the order
 * the array gives, top-left first.
 */
export function CastGrid({
  players,
  ghostIds,
  meId,
  currentClueGiverId,
  justArrived = false,
}: CastGridProps): JSX.Element {
  const { t } = useTranslation();
  const ghostIdSet = new Set(ghostIds);

  return (
    /* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver strip list semantics from <ul> when list-style:none is applied (see CastGrid.css); explicit role="list" restores them. */
    <ul
      className="susdle-cluegiving-cast-grid"
      role="list"
      aria-label={t('cluegiving.cast.ariaLabel')}
    >
      {players.map((player) => {
        const isActiveSpeaker = player.id === currentClueGiverId;
        /* "Fellow" describes the relationship to the local Ghost, so the
           local Ghost's OWN tile never carries the mark. */
        const isFellow = ghostIdSet.has(player.id) && player.id !== meId;
        return (
          <li key={player.id}>
            <span
              className="susdle-cluegiving-cast-cell"
              data-active-speaker={isActiveSpeaker ? 'yes' : 'no'}
              /* The lift is the active-speaker signal; expose it to AT users
                 via an aria-label on the cell when active so SR readers
                 hear "Maya, writing the next clue" rather than just "Maya". */
              aria-label={
                isActiveSpeaker
                  ? t('cluegiving.cast.activeSpeaker', { name: player.name })
                  : undefined
              }
            >
              <AvatarFrame
                player={player}
                isHost={false}
                isFellowGhost={isFellow}
                away={!player.connected}
              />
              <ClueSlip
                clue={player.clue}
                ownerName={player.name}
                suppressInk={justArrived}
                testId={`clue-slip-${player.id}`}
              />
            </span>
          </li>
        );
      })}
    </ul>
  );
}
